import type { CollabListRow, CollabRoom } from "@creators/backend/lib/collabs/queries";
import { isTerminal, type CollabStatus } from "@creators/backend/lib/collabs/state-machine";
import { isHistoryCollab } from "./collab-status";

export type CollabTab = "all" | "action" | "active" | "completed" | "closed";

export const COLLAB_TABS: { id: CollabTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "action", label: "Needs you" },
  { id: "active", label: "In progress" },
  { id: "completed", label: "Completed" },
  { id: "closed", label: "Closed" },
];

const CLOSED_STATUSES: CollabStatus[] = ["cancelled", "expired"];

export { isHistoryCollab };

export function filterActiveCollabs(collabs: CollabListRow[]): CollabListRow[] {
  return collabs.filter((collab) => !isHistoryCollab(collab));
}

/** Same turn rule as CollabList / statusLine — money states outrank work state. */
export function collabNeedsYou(collab: CollabListRow): boolean {
  if (isTerminal(collab.displayStatus) || CLOSED_STATUSES.includes(collab.displayStatus)) {
    return false;
  }
  // Basic opening offers stay quiet — no Needs you until the thread moves on.
  if (collab.boostTier === "basic" && collab.status === "offer_pending") {
    return false;
  }
  // Marked-as-sent → creator must confirm. Due → brand must pay.
  if (collab.paymentAwaitingConfirm) return collab.yourRole === "creator";
  if (collab.paymentDue) return collab.yourRole === "brand";
  return collab.pendingParty === collab.yourRole;
}

/** Room-shaped version of `collabNeedsYou` for Chat / Details chrome. */
export function roomNeedsYou(
  room: CollabRoom,
  yourRole: "creator" | "brand" | "admin",
): boolean {
  if (yourRole === "admin") return false;
  const status = room.collab.status as CollabStatus;
  if (isTerminal(status) || CLOSED_STATUSES.includes(status)) return false;
  if (room.collab.boostTier === "basic" && status === "offer_pending") return false;
  const paymentAwaitingConfirm = room.milestones.some((m) => m.status === "paid");
  if (paymentAwaitingConfirm) return yourRole === "creator";
  const paymentDue = room.milestones.some((m) => m.status === "due");
  if (paymentDue) return yourRole === "brand";
  return room.collab.pendingParty === yourRole;
}

export function collabMatchesTab(collab: CollabListRow, tab: CollabTab): boolean {
  const status = collab.displayStatus;

  switch (tab) {
    case "all":
      return true;
    case "action":
      return (
        !isTerminal(status) &&
        !CLOSED_STATUSES.includes(status) &&
        collabNeedsYou(collab)
      );
    case "active":
      return (
        !isTerminal(status) &&
        !CLOSED_STATUSES.includes(status) &&
        status !== "completed" &&
        !collabNeedsYou(collab)
      );
    case "completed":
      return status === "completed";
    case "closed":
      return CLOSED_STATUSES.includes(status);
    default:
      return true;
  }
}

export function countCollabsByTab(collabs: CollabListRow[]): Record<CollabTab, number> {
  return COLLAB_TABS.reduce(
    (counts, tab) => {
      counts[tab.id] = collabs.filter((collab) => collabMatchesTab(collab, tab.id)).length;
      return counts;
    },
    { all: 0, action: 0, active: 0, completed: 0, closed: 0 } as Record<CollabTab, number>,
  );
}

export function parseCollabTab(value: string | undefined): CollabTab {
  if (value && COLLAB_TABS.some((tab) => tab.id === value)) return value as CollabTab;
  return "all";
}

// ---------------------------------------------------------------------------
// Which side of the collab you are on
// ---------------------------------------------------------------------------

export type CollabRole = "all" | "delivering" | "booked";

export const COLLAB_ROLES: { value: CollabRole; label: string }[] = [
  { value: "all", label: "All" },
  { value: "delivering", label: "Creator" },
  { value: "booked", label: "Collaborator" },
];

export function parseCollabRole(value: string | undefined): CollabRole {
  return COLLAB_ROLES.some((role) => role.value === value) ? (value as CollabRole) : "all";
}

export function filterCollabsByRole(collabs: CollabListRow[], role: CollabRole): CollabListRow[] {
  if (role === "all") return collabs;
  const side = role === "delivering" ? "creator" : "brand";
  return collabs.filter((collab) => collab.yourRole === side);
}

export function filterCollabsByTab(collabs: CollabListRow[], tab: CollabTab): CollabListRow[] {
  return collabs.filter((collab) => collabMatchesTab(collab, tab));
}
